<template>
  <div class="tw:flex tw:flex-col tw:gap-4" data-testid="reg-company-basics">
    <span class="tw:text-13 tw:font-semibold tw:text-gray-900">{{ $t('registration.company.section_basics') }}</span>

    <div class="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2">
      <LeadInput
        v-model="name"
        type="text"
        autocomplete="organization"
        :label="$t('registration.company.name')"
        :errors="errors.name"
        required
        data-testid="reg-company-name"
        @blur="emit('nameBlur')">
        <template #iconStart><IconBuilding :size="16" /></template>
        <template v-if="nameConfirmed" #iconEnd>
          <IconRosetteDiscountCheck :size="16" color="var(--tw-color-success-700)" />
        </template>
      </LeadInput>

      <LeadInput
        v-model="careersPage"
        type="text"
        autocomplete="url"
        :label="$t('registration.company.careers_page')"
        :errors="errors.careersPage"
        data-testid="reg-company-url">
        <template #prefix>https://</template>
        <template #hint>{{ $t('registration.company.careers_page_hint') }}</template>
      </LeadInput>
    </div>

    <div class="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2">
      <LeadSelect
        v-model="industry"
        :options="industryOptions"
        :label="$t('registration.company.industry')"
        :placeholder="$t('registration.company.industry_placeholder')"
        :errors="errors.industry"
        required
        data-testid="reg-company-industry" />

      <LeadSelect
        v-model="companySize"
        :options="sizeOptions"
        :label="$t('registration.company.size')"
        :placeholder="$t('registration.company.size_placeholder')"
        :errors="errors.companySize"
        required
        data-testid="reg-company-size" />
    </div>
  </div>
</template>

<script setup lang="ts">
/** The company step's basics section: name, careers page, industry and size. */
import { computed } from 'vue';

import LeadInput from '@/components/lead-design/LeadInput.vue';
import LeadSelect, { type LeadSelectOption } from '@/components/lead-design/LeadSelect.vue';
import translator from '@/locale/translator';
import type { CompanySize, Industry, RegistrationDraft } from '@/types/registration';
import { IconBuilding, IconRosetteDiscountCheck } from '@tabler/icons-vue';

const INDUSTRIES: Industry[] = ['health', 'trades', 'it', 'retail', 'finance', 'public'];
const SIZES: CompanySize[] = ['10', '50', '200', '1000', '1001'];

/* ----------------------------------- STATE ---------------------------------- */
defineProps<{
  errors: { name: string; careersPage: string; industry: string; companySize: string };
  /** Whether the name is the exact one the register confirmed the UID against. */
  nameConfirmed: boolean;
}>();

/** Lets the address section re-check the UID once the name settles. */
const emit = defineEmits<{ nameBlur: [] }>();

const draft = defineModel<RegistrationDraft['company']>('draft', { required: true });

/* --------------------------------- COMPUTED --------------------------------- */
const name = computed({
  get: () => draft.value.name,
  set: (value: string) => {
    draft.value.name = value;
  },
});

// stored schemeless: strip a pasted scheme so the template's https:// prefix isn't doubled
const careersPage = computed({
  get: () => draft.value.careersPage,
  set: (value: string) => {
    draft.value.careersPage = value.replace(/^https?:\/\//i, '');
  },
});

// LeadSelect's v-model is untyped per consumer; the cast is safe as values come from the options below
const industry = computed({
  get: () => draft.value.industry,
  set: (value: string | string[] | null) => {
    draft.value.industry = (value ?? '') as Industry | '';
  },
});

const companySize = computed({
  get: () => draft.value.companySize,
  set: (value: string | string[] | null) => {
    draft.value.companySize = (value ?? '') as CompanySize | '';
  },
});

const industryOptions = computed<LeadSelectOption[]>(() =>
  INDUSTRIES.map((value) => ({ value, label: translator.get(`registration.company.industry.${value}`) }))
);

const sizeOptions = computed<LeadSelectOption[]>(() =>
  SIZES.map((value) => ({ value, label: translator.get(`registration.company.size.${value}`) }))
);
</script>
